import { BOOLEAN_BYTES_PER_ELEMENT, BusMessage, calcStringByteLength, MessageFactory, UID_BYTES_PER_ELEMENT } from "flavr-common";
/**
 * Announces that a peer entry was created for the given client.
 */
export class PeerCreatedMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.clientId) + Uint32Array.BYTES_PER_ELEMENT;
    }
    constructor(clientId, clientIdx) {
        super();
        this.clientId = clientId;
        this.clientIdx = clientIdx;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
        this.clientIdx = stream.uint32(this.clientIdx);
    }
    static is(msg) {
        return msg.type === PeerCreatedMessage.typestatic;
    }
}
PeerCreatedMessage.typestatic = "PeerCreatedMessage";
PeerCreatedMessage.subjectstatic = "general";
MessageFactory.register(PeerCreatedMessage);
/**
 * Announces that a peer joined and should be tracked by client-side peer systems.
 */
export class PeerAddedMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.clientId) + Uint32Array.BYTES_PER_ELEMENT;
    }
    constructor(clientId, clientIdx) {
        super();
        this.clientId = clientId;
        this.clientIdx = clientIdx;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
        this.clientIdx = stream.uint32(this.clientIdx);
    }
    static is(msg) {
        return msg.type === PeerAddedMessage.typestatic;
    }
}
PeerAddedMessage.typestatic = "PeerAddedMessage";
PeerAddedMessage.subjectstatic = "general";
MessageFactory.register(PeerAddedMessage);
/**
 * Announces that a peer left and should be removed from client-side peer state.
 */
export class PeerRemovedMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.clientId);
    }
    constructor(clientId) {
        super();
        this.clientId = clientId;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
    }
    static is(msg) {
        return msg.type === PeerRemovedMessage.typestatic;
    }
}
PeerRemovedMessage.typestatic = "PeerRemovedMessage";
PeerRemovedMessage.subjectstatic = "general";
MessageFactory.register(PeerRemovedMessage);
/**
 * Reports a peer networking state transition such as connecting, connected or disconnected.
 */
export class PeerStateChangedMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    constructor(clientId, eventType, state) {
        super();
        this.clientId = clientId;
        this.eventType = eventType;
        this.state = state;
    }
    serialization() { }
    static is(msg) {
        return msg.type === PeerStateChangedMessage.typestatic;
    }
}
PeerStateChangedMessage.typestatic = "PeerStateChangedMessage";
PeerStateChangedMessage.subjectstatic = "local";
MessageFactory.register(PeerStateChangedMessage);
/**
 * Publishes the local user's avatar and identity data for peer synchronization.
 */
export class LocalUserInfoMessage extends BusMessage {
    get byteLength() {
        let byteLength = calcStringByteLength(this.clientId) +
            Uint32Array.BYTES_PER_ELEMENT + // clientIdx
            BOOLEAN_BYTES_PER_ELEMENT; // hasIdentity
        byteLength +=
            calcStringByteLength(this.avatar.imageUrl) +
                Uint32Array.BYTES_PER_ELEMENT + // baseColor
                Uint8Array.BYTES_PER_ELEMENT; // style
        byteLength += Uint32Array.BYTES_PER_ELEMENT; // accessoiresLength
        if (this.avatar.accessoires) {
            byteLength +=
                this.avatar.accessoires.length *
                    (Uint32Array.BYTES_PER_ELEMENT + // id
                        Uint32Array.BYTES_PER_ELEMENT); // color
        }
        if (this.identity !== undefined) {
            byteLength += calcStringByteLength(this.identity.username);
            byteLength += calcStringByteLength(this.identity.firstname);
            byteLength += calcStringByteLength(this.identity.lastname);
        }
        return byteLength;
    }
    constructor(clientId, clientIdx, avatar, identity) {
        super();
        this.clientId = clientId;
        this.clientIdx = clientIdx;
        this.avatar = avatar;
        this.identity = identity;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
        this.clientIdx = stream.uint32(this.clientIdx);
        if (stream.isReading) {
            this.avatar = {};
        }
        this.avatar.imageUrl = stream.utf16String(this.avatar.imageUrl);
        this.avatar.baseColor = stream.uint32(this.avatar.baseColor);
        this.avatar.style = stream.uint8(this.avatar.style);
        const accessoiresLength = stream.uint32(this.avatar.accessoires ? this.avatar.accessoires.length : 0);
        if (accessoiresLength > 0) {
            if (!this.avatar.accessoires) {
                this.avatar.accessoires = [];
            }
            for (let i = 0; i < accessoiresLength; i++) {
                this.avatar.accessoires[i].id = stream.uint32(this.avatar.accessoires[i].id);
                this.avatar.accessoires[i].color = stream.uint32(this.avatar.accessoires[i].color);
            }
        }
        if (stream.booleanSafe(this.identity !== undefined)) {
            if (!this.identity) {
                this.identity = {
                    username: "",
                    firstname: "",
                    lastname: ""
                };
            }
            this.identity.username = stream.utf16String(this.identity.username);
            this.identity.firstname = stream.utf16String(this.identity.firstname);
            this.identity.lastname = stream.utf16String(this.identity.lastname);
        }
    }
    static is(msg) {
        return msg.type === LocalUserInfoMessage.typestatic;
    }
}
LocalUserInfoMessage.typestatic = "LocalUserInfoMessage";
LocalUserInfoMessage.subjectstatic = "general";
MessageFactory.register(LocalUserInfoMessage);
/**
 * Shares a peer's avatar, identity and optional custom username with other modules.
 */
export class UserInfoMessage extends BusMessage {
    get byteLength() {
        let byteLength = calcStringByteLength(this.clientId) +
            Uint32Array.BYTES_PER_ELEMENT + // clientIdx
            BOOLEAN_BYTES_PER_ELEMENT + // hasIdentity
            calcStringByteLength(this.customUsername);
        byteLength +=
            calcStringByteLength(this.avatar.imageUrl) +
                Uint32Array.BYTES_PER_ELEMENT + // baseColor
                Uint8Array.BYTES_PER_ELEMENT; // style
        if (this.identity !== undefined) {
            byteLength += calcStringByteLength(this.identity.username);
            byteLength += calcStringByteLength(this.identity.firstname);
            byteLength += calcStringByteLength(this.identity.lastname);
        }
        return byteLength;
    }
    constructor(clientId, clientIdx, avatar, identity, customUsername) {
        super();
        this.clientId = clientId;
        this.clientIdx = clientIdx;
        this.avatar = avatar;
        this.identity = identity;
        this.customUsername = customUsername;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
        this.clientIdx = stream.uint32(this.clientIdx);
        if (stream.isReading) {
            this.avatar = {};
        }
        this.avatar.imageUrl = stream.utf16String(this.avatar.imageUrl);
        this.avatar.baseColor = stream.uint32(this.avatar.baseColor);
        this.avatar.style = stream.uint8(this.avatar.style);
        if (stream.booleanSafe(this.identity !== undefined)) {
            if (!this.identity) {
                this.identity = {
                    username: "",
                    firstname: "",
                    lastname: ""
                };
            }
            this.identity.username = stream.utf16String(this.identity.username);
            this.identity.firstname = stream.utf16String(this.identity.firstname);
            this.identity.lastname = stream.utf16String(this.identity.lastname);
        }
        this.customUsername = stream.utf16StringSafe(this.customUsername);
    }
    static is(msg) {
        return msg.type === UserInfoMessage.typestatic;
    }
}
UserInfoMessage.typestatic = "UserInfoMessage";
UserInfoMessage.subjectstatic = "general";
MessageFactory.register(UserInfoMessage);
/**
 * Reports that an avatar peer moved to a room with a different relative room depth.
 */
export class AvatarPeerRoomChangedMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + Int32Array.BYTES_PER_ELEMENT + BOOLEAN_BYTES_PER_ELEMENT;
    }
    constructor(uid, roomDepthDiff, audible) {
        super();
        this.uid = uid;
        this.roomDepthDiff = roomDepthDiff;
        this.audible = audible;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.roomDepthDiff = stream.int32(this.roomDepthDiff);
        this.audible = stream.boolean(this.audible);
    }
    static is(msg) {
        return msg.type === AvatarPeerRoomChangedMessage.typestatic;
    }
}
AvatarPeerRoomChangedMessage.typestatic = "AvatarPeerRoomChanged";
AvatarPeerRoomChangedMessage.subjectstatic = "general";
MessageFactory.register(AvatarPeerRoomChangedMessage);
/**
 * Toggles the visibility of a peer avatar mesh.
 */
export class AvatarMeshVisibilityMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + BOOLEAN_BYTES_PER_ELEMENT; // visibility
    }
    constructor(uid, visibility) {
        super();
        this.uid = uid;
        this.visibility = visibility;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.visibility = stream.boolean(this.visibility);
    }
    static is(msg) {
        return msg.type === AvatarMeshVisibilityMessage.typestatic;
    }
}
AvatarMeshVisibilityMessage.typestatic = "AvatarMeshVisibilityMessage";
AvatarMeshVisibilityMessage.subjectstatic = "general";
MessageFactory.register(AvatarMeshVisibilityMessage);
